import React, { forwardRef, useEffect, useLayoutEffect, useRef, MutableRefObject } from 'react'
import Quill from 'quill'
import { isAndroid } from 'react-device-detect'
import { normalizeExistingDelta } from './quill/quillHelpers'
import { isEqual, cloneDeep } from 'lodash'
import styles from './quill-editor.module.scss'
import { getStableText } from './quill/quillHelpers'

// Import Delta directly from Quill
const Delta = Quill.import('delta')

// Supported formats
export const formats = [
    'color',
    'bold',
    'italic',
    'link',
    'mention',
    'header',
    'code',
    'strike',
    'script',
    'underline',
    'blockquote',
    'indent',
    'list',
    'align',
    'direction',
    'code-block',
    'formula',
    'image',
    'video',
    'more-button',
    'barrier',
]

// Configuration for Quill modules
export const getQuillModulesConfig = (props: any = {}) => {
    const enterPostsImmediatelyEnabled = (window as any)?.gon?.currentUser?.features?.some?.(
        f => f.name === 'enter_key_posts_immediately',
    )

    const config = {
        keyboard: {
            bindings: {
                tab: false,
                enter: {
                    key: 'Enter',
                    shiftKey: true,
                    handler: () => {
                        return true
                    },
                },
            },
        },
        autoLinks: true,
        clipboard: {
            matchVisual: false,
        },
    }

    // Handle enter key posts immediately feature
    if (enterPostsImmediatelyEnabled || props.type === 'search') {
        delete config.keyboard.bindings.enter.shiftKey
    }

    // Handle import case
    if (props.type === 'import') {
        delete config.keyboard.bindings.enter
    }

    return config
}

// Editor is an uncontrolled React component
const QuillEditor = forwardRef<
    Quill,
    {
        defaultValue?: any
        readOnly?: boolean
        placeholder?: string
        lastKeyPressed?: string
        isProgrammaticChangeInProgress?: boolean
        programmaticChangeRef?: React.MutableRefObject<boolean>
        onTextChange?: (delta: any, oldContents: any, source: string) => void
        onSelectionChange?: (range: any, oldRange: any, source: string) => void
        onKeyDown?: (quill: Quill, event: KeyboardEvent) => void
        onFocus?: (quill: Quill) => void
        onBlur?: (quill: Quill) => void
        onEditorLoad?: (quill: Quill) => void
        style?: React.CSSProperties
        theme?: string | null
        [key: string]: any
    }
>((props, ref) => {
    const {
        defaultValue,
        readOnly = false,
        placeholder = '',
        lastKeyPressed,
        isProgrammaticChangeInProgress,
        programmaticChangeRef,
        onTextChange,
        onSelectionChange,
        onKeyDown,
        onFocus,
        onBlur,
        onEditorLoad,
        style,
        theme = null,
        ...otherProps
    } = props

    const containerRef = useRef<HTMLDivElement>(null)
    const editorRef = useRef<HTMLDivElement | null>(null)
    const defaultValueRef = useRef(defaultValue)
    const onTextChangeRef = useRef(onTextChange)
    const onSelectionChangeRef = useRef(onSelectionChange)
    const onKeyDownRef = useRef(onKeyDown)
    const onFocusRef = useRef(onFocus)
    const onBlurRef = useRef(onBlur)
    const previousDefaultValueRef = useRef(defaultValue)

    // Ref to store the last known good Quill Delta, used by MutationObserver
    const lastKnownGoodDeltaRef = useRef<any>(null)

    // Keep track of the last key pressed via prop from CommonEditor
    const lastKeyPressedRef = useRef(lastKeyPressed)

    // Ref holding a pending verification animation frame ID.
    const pendingVerifyRAFRef = useRef<number | null>(null)

    // Reference for programmatic change flag: use provided ref for immediate sync, else internal one
    const internalProgrammaticRef = useRef(isProgrammaticChangeInProgress)
    const isProgrammaticChangeInProgressRef = programmaticChangeRef ?? internalProgrammaticRef

    // Keep callback refs updated with useLayoutEffect
    useLayoutEffect(() => {
        onTextChangeRef.current = onTextChange
        onSelectionChangeRef.current = onSelectionChange
        onKeyDownRef.current = onKeyDown
        onFocusRef.current = onFocus
        onBlurRef.current = onBlur
    })

    // Enable/disable the editor when readOnly changes
    useEffect(() => {
        if (ref) {
            // Handle both callback and object refs
            const quillInstance =
                typeof ref === 'function'
                    ? null // Can't access a callback ref's current
                    : ref.current

            if (quillInstance) {
                quillInstance.enable(!readOnly)
            }
        }
    }, [ref, readOnly])

    // Watch for changes to defaultValue and update editor content when needed
    useEffect(() => {
        // Skip effect during initial render
        if (defaultValueRef.current === defaultValue) return

        // Get quill instance from ref
        const quillInstance = typeof ref === 'function' ? null : ref.current
        if (!quillInstance) return // Exit if quill instance isn't ready

        const normalizedPrevValue = normalizeExistingDelta(
            previousDefaultValueRef.current || { ops: [] },
        )
        const normalizedNewValue = normalizeExistingDelta(defaultValue || { ops: [] })

        // Get current editor content for comparison
        const currentEditorContent = normalizeExistingDelta(quillInstance.getContents())

        // Only update if there's a meaningful difference between:
        // 1. The new defaultValue and the previous defaultValue AND
        // 2. The new defaultValue and the current actual content in the editor
        const hasChangedFromPrevious = !isEqual(normalizedPrevValue, normalizedNewValue)
        const isDifferentFromCurrent = !isEqual(currentEditorContent, normalizedNewValue)

        // Log comparison results for debugging
        // console.log('[QuillEditor defaultValue useEffect]', {
        //     hasChangedFromPrevious,
        //     isDifferentFromCurrent,
        //     // normalizedNewValue: JSON.stringify(normalizedNewValue),
        //     // currentEditorContent: JSON.stringify(currentEditorContent),
        // });

        if (hasChangedFromPrevious && isDifferentFromCurrent) {
            // console.log('[QuillEditor defaultValue useEffect] - Applying new defaultValue');
            // Store current selection
            const currentSelection = quillInstance.getSelection()

            // Update content
            quillInstance.setContents(normalizedNewValue) // Source: 'api' implicitly

            // Restore selection if it existed
            if (currentSelection) {
                setTimeout(() => {
                    // Avoid setting selection if editor no longer has focus
                    if (quillInstance.hasFocus()) {
                        quillInstance.setSelection(currentSelection)
                    }
                }, 0)
            }
        } else {
            // console.log('[QuillEditor defaultValue useEffect] - Skipping update, content matches current or previous');
        }
        // Update the reference to previous value regardless of update,
        // to correctly track subsequent changes.
        previousDefaultValueRef.current = defaultValue
    }, [defaultValue, ref])

    // Keep internal ref in sync when using internal tracking
    useEffect(() => {
        if (!programmaticChangeRef) {
            isProgrammaticChangeInProgressRef.current = props.isProgrammaticChangeInProgress
        }
        // If a programmatic change just FINISHED, update our baseline for the MutationObserver
        if (!props.isProgrammaticChangeInProgress && ref) {
            const quillInstance =
                typeof ref === 'function'
                    ? null // Can't access a callback ref's current
                    : ref.current
            if (quillInstance) {
                lastKnownGoodDeltaRef.current = quillInstance.getContents()
            }
        } else if (props.isProgrammaticChangeInProgress) {
        }
    }, [props.isProgrammaticChangeInProgress]) // only reacts when prop changes since ref handled separately

    // Initialize Quill on mount
    useEffect(() => {
        const container = containerRef.current
        if (!container) {
            console.error('[QuillEditor] Main Initialization useEffect: Container ref NOT FOUND.')
            return
        }

        // Create editor container dynamically
        const editorContainer = container.appendChild(container.ownerDocument.createElement('div'))

        // Store a reference to the editor container
        editorRef.current = editorContainer

        // Apply any styles passed to the component
        if (style) {
            Object.assign(editorContainer.style, style)
        }

        editorContainer.className = 'quill-editor-content'

        // Configure Quill
        const editorModules = getQuillModulesConfig(props)

        // Create the Quill instance
        const quill = new Quill(editorContainer, {
            theme: theme,
            placeholder: readOnly ? '' : placeholder,
            readOnly: readOnly,
            modules: editorModules,
            formats: formats,
            // Add bounds property for mobile to improve positioning
            bounds: container,
        })

        /**********************
         * ANDROID IME HELPERS *
         * ------------------- *
         * On many Android keyboards the text remains in an "underlined"  (composing)
         * state until the IME finalises the composition.  If the keyboard is
         * closed before that happens, the browser may discard the not-yet-committed
         * text, so Quill never sees it.  We mitigate this by:
         *   1. Tracking when we are inside an IME composition (compositionstart / end)
         *   2. Forcing Quill to synchronise its model with the DOM via
         *      quill.update('user') as soon as compositionend fires.
         *   3. Doing the same when the editor blurs while a composition is active.
         *
         * This is intentionally lightweight – it does not attempt the full
         * ProseMirror style reconciliation dance, but it already fixes the
         * common "typed text disappears after closing the keyboard" issue on
         * most Android devices we tested.
         **********************/

        let isComposingAndroid = false
        // Timestamp used to ignore stray delete events that some Android IMEs
        // emit shortly *after* compositionend.  See handleBeforeInputGuard.
        let lastCompositionEndTs = 0
        let imeMutationObserver: MutationObserver | null = null

        // Define here so cleanup can access the same references
        const handleCompositionStart = () => {
            isComposingAndroid = true
        }

        const handleCompositionEnd = () => {
            // Mark composition finished _before_ flushing so other handlers know
            isComposingAndroid = false
            lastCompositionEndTs = Date.now()
            // Defer the update to the next animation frame so the DOM reflects
            // the final composed text before Quill reads it.
            requestAnimationFrame(() => {
                try {
                    quill.update('user')
                    lastKnownGoodDeltaRef.current = quill.getContents() // Snapshot after update

                    // Start MutationObserver to watch for silent DOM deletions
                    if (imeMutationObserver) imeMutationObserver.disconnect() // Clear previous
                    imeMutationObserver = new MutationObserver(mutationsList => {
                        for (const mutation of mutationsList) {
                            if (
                                mutation.type === 'childList' ||
                                mutation.type === 'characterData'
                            ) {
                                const currentDelta = quill.getContents()

                                if (isProgrammaticChangeInProgressRef.current) {
                                    lastKnownGoodDeltaRef.current = currentDelta
                                } else if (
                                    currentDelta.length() < lastKnownGoodDeltaRef.current?.length()
                                ) {
                                    const preShrinkBaseline = cloneDeep(
                                        lastKnownGoodDeltaRef.current,
                                    ) // Capture baseline at point of shrink detection
                                    // Possible phantom deletion.  Verify on the next animation frame whether
                                    // Quill fires a corresponding TEXT_CHANGE.  If not, we will revert.

                                    if (pendingVerifyRAFRef.current === null) {
                                        pendingVerifyRAFRef.current = requestAnimationFrame(() => {
                                            pendingVerifyRAFRef.current = null
                                            const afterFrameDelta = quill.getContents()
                                            // Compare with the captured preShrinkBaseline
                                            if (
                                                afterFrameDelta.length() <
                                                preShrinkBaseline.length()
                                            ) {
                                                quill.setContents(
                                                    preShrinkBaseline,
                                                    Quill.sources.USER,
                                                ) // Revert to captured baseline
                                                // The TEXT_CHANGE from above setContents will be handled by our TEXT_CHANGE handler,
                                                // which will see source === USER and update lastKnownGoodDeltaRef.
                                            } else {
                                                // Content is no longer shorter than the (potentially updated) baseline.
                                                // This implies a legitimate change occurred or the baseline was updated.
                                                // Accept the current state.
                                                // It's possible a legitimate TEXT_CHANGE updated the main LKGDelta separately,
                                                // or this afterFrameDelta is fine. Update the main LKGDelta to this latest good state.
                                                lastKnownGoodDeltaRef.current = afterFrameDelta
                                            }
                                        })
                                    }
                                } else {
                                    lastKnownGoodDeltaRef.current = currentDelta
                                }
                            }
                        }
                    })
                    imeMutationObserver.observe(quill.root, {
                        childList: true,
                        characterData: true,
                        subtree: true,
                    })

                    // Stop observing after a short period
                    setTimeout(() => {
                        if (imeMutationObserver) {
                            imeMutationObserver.disconnect()
                            imeMutationObserver = null
                        }
                    }, 1500)
                } catch (_) {
                    /* safeguard – never crash on update */
                }
            })
        }

        const isAndroidCurrentEvaluation = isAndroid

        // Guard against stray deleteContentBackward fired by some keyboards (Android specific)
        const handleBeforeInputGuard = (ev: InputEvent) => {
            const t = ev.inputType || ''
            if (t.startsWith('delete')) {
                const withinGrace = Date.now() - lastCompositionEndTs < 800
                if (withinGrace) {
                    ev.preventDefault()
                    ev.stopPropagation()
                    return
                }
            }
        }

        if (isAndroidCurrentEvaluation) {
            quill.root.addEventListener('compositionstart', handleCompositionStart)
            quill.root.addEventListener('beforeinput', handleBeforeInputGuard, true)
            quill.root.addEventListener('compositionend', handleCompositionEnd)
        } else {
        }

        // General event handlers
        const handleKeyDown = e => {
            // For props.onKeyDown
            if (onKeyDownRef.current) {
                lastKeyPressedRef.current = e.key // Keep this for MO
                onKeyDownRef.current(quill, e)
            }
        }

        let lastInputWasAt = false // For Android @ detection
        let handleAndroidInput = () => {} // Define for Android specific input
        if (isAndroidCurrentEvaluation) {
            handleAndroidInput = () => {
                const selection = quill.getSelection()
                if (!selection) return
                const text = getStableText(quill)
                const pos = selection.index
                if (lastInputWasAt) {
                    lastInputWasAt = false
                    const delta = new Delta().retain(quill.getLength())
                    quill.updateContents(delta, 'user')
                    quill.setSelection(pos + 1, 0, 'user')
                }
                if (pos > 0 && text[pos - 1] === '@') {
                    lastInputWasAt = true
                }
            }
        }

        const handleFocus = () => {
            if (onFocusRef.current) {
                requestAnimationFrame(() => {
                    onFocusRef.current(quill)
                })
            }
        }

        const handleBlur = () => {
            if (isAndroid && isComposingAndroid) {
                isComposingAndroid = false
                requestAnimationFrame(() => {
                    try {
                        quill.update('user')
                    } catch (_) {
                        /* ignore */
                    }
                })
            }
            if (onBlurRef.current) {
                requestAnimationFrame(() => {
                    onBlurRef.current(quill)
                })
            }
        }

        // --- LISTENER ATTACHMENTS (ALL COMMENTED OUT INITIALLY) ---
        quill.on(Quill.events.TEXT_CHANGE, (delta, oldContents, source) => {
            // Use all args
            const currentContents = quill.getContents()

            if (pendingVerifyRAFRef.current !== null) {
                // An RAF is PENDING (meaning a *previous* mutation was suspicious).
                // Handle this TEXT_CHANGE in context of that pending verification.
                if (source === Quill.sources.USER) {
                    // If user action (our revert, or typing) resolves the pending issue.
                    const lkgLength = lastKnownGoodDeltaRef.current?.length() || 0
                    if (
                        currentContents.length() >= lkgLength ||
                        isEqual(currentContents.ops, lastKnownGoodDeltaRef.current.ops)
                    ) {
                        lastKnownGoodDeltaRef.current = currentContents
                        cancelAnimationFrame(pendingVerifyRAFRef.current)
                        pendingVerifyRAFRef.current = null
                    } else {
                        // Don't update LKGDelta; let the MO's RAF (using its own preShrinkBaseline) decide.
                    }
                } else {
                    // Non-USER source during a pending RAF
                    // Don't update LKGDelta.
                }
            } else {
                // NO RAF is PENDING. This is a new event.
                if (source === Quill.sources.USER) {
                    // Standard user typing/action. Always update LKGDelta.
                    lastKnownGoodDeltaRef.current = currentContents
                } else {
                    // Non-USER action (e.g., 'api', 'silent').
                    // THIS could be the start of a phantom delete that Quill itself is reporting.
                    // If it's a shrink, DON'T update LKGDelta here. Let the MO be the first to see it
                    // against the *previous* LKGDelta.
                    const lkgLength = lastKnownGoodDeltaRef.current?.length() || 0
                    if (currentContents.length() < lkgLength) {
                        // DO NOT UPDATE lastKnownGoodDeltaRef.current
                        // The MutationObserver will see this currentContents, compare it to the *existing, longer*
                        // lastKnownGoodDeltaRef, detect a shrink, clone LKG, and start its RAF.
                    } else {
                        // Non-USER, but not a shrink (or LKGDelta was null). Safe to update.
                        lastKnownGoodDeltaRef.current = currentContents
                    }
                }
            }
            onTextChangeRef.current?.(delta, oldContents, source)
        })

        quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
            onSelectionChangeRef.current?.(...args)
        })

        quill.root.addEventListener('keydown', handleKeyDown)

        if (isAndroidCurrentEvaluation) {
            quill.root.addEventListener('input', handleAndroidInput)
        }

        quill.root.addEventListener('focus', handleFocus)
        quill.root.addEventListener('blur', handleBlur)

        // Expose the Quill instance directly through ref
        if (typeof ref === 'function') {
            ref(quill)
        } else if (ref) {
            ;(ref as MutableRefObject<Quill>).current = quill
        } else {
        }

        if (defaultValueRef.current) {
            const normalizedValue = normalizeExistingDelta(defaultValueRef.current)
            quill.setContents(normalizedValue)
        }

        if (onEditorLoad) {
            onEditorLoad(quill)
        }

        return () => {
            // Comment out all removeEventListener calls to match commented attachments
            quill.root.removeEventListener('focus', handleFocus)
            quill.root.removeEventListener('blur', handleBlur)
            quill.root.removeEventListener('keydown', handleKeyDown)
            if (isAndroidCurrentEvaluation) {
                quill.root.removeEventListener('beforeinput', handleBeforeInputGuard, true)
                if (handleAndroidInput) {
                    // Check if handleAndroidInput was defined
                    quill.root.removeEventListener('input', handleAndroidInput)
                }
                quill.root.removeEventListener('compositionstart', handleCompositionStart)
                quill.root.removeEventListener('compositionend', handleCompositionEnd)
            }

            if (imeMutationObserver) {
                imeMutationObserver.disconnect()
                imeMutationObserver = null
            }
            if (pendingVerifyRAFRef.current !== null) {
                cancelAnimationFrame(pendingVerifyRAFRef.current)
                pendingVerifyRAFRef.current = null
            }

            if (typeof ref === 'function') {
                ref(null)
            } else if (ref) {
                ;(ref as MutableRefObject<Quill>).current = null
            }
            if (container) {
                container.innerHTML = ''
            }
            editorRef.current = null
        }
    }, [ref, readOnly])

    return (
        <div
            ref={containerRef}
            className={`quill-container ${styles.quillContainer}`}
            {...otherProps}
        />
    )
})

// Add displayName for better debugging
QuillEditor.displayName = 'QuillEditor'

export default QuillEditor
